import { trackingSurveyEvent } from '@/api/tracking';
import {
  DocType,
  FilterInfo,
  ShareSurveysKind,
  SpecifiedMethod,
  SurveyEventInfo,
  SurveyExecutionFrom,
  SurveyPageName,
  SurveyPromptTemplateTrackingInfo,
  SurveyPromptType,
  SurveyType,
} from '@/types';
import {
  SEARCH_TO_SURVEY_GROUP_MARKS_QUERY,
  SEARCH_TO_SURVEY_USER_MARKS_QUERY,
  SURVEY_FEATURE_NAME,
} from '@/utils/survey/constants';

/**
 * マイページやグループから来たときにそのままデフォルトのクエリが使われていたかどうかを判定する
 */
export const isDefaultSurveyQuery = (
  query: string,
  from: SurveyExecutionFrom,
) => {
  return (
    (from === 'my_marks_page' && query === SEARCH_TO_SURVEY_USER_MARKS_QUERY) ||
    ((from === 'group_marks_page' || from === 'group_activity_page') &&
      query === SEARCH_TO_SURVEY_GROUP_MARKS_QUERY)
  );
};

/**
 * Survey生成イベントのトラッキング
 */
export const trackSubmitSurvey = async (
  from: SurveyExecutionFrom,
  sessionId: string,
  rank: number,
  filterInfo: FilterInfo,
  dataSources: (DocType | 'user_document')[],
  surveyType: SurveyType,
  deepMode: boolean,
  parentRequestId: string | null,
  previousRequestId: string | null,
  method?: SpecifiedMethod,
  fromRelatedQuestion?: boolean,
  sampleSessionId?: string,
  shareSurveyLastRequestId?: string,
  shareSurveyKind?: ShareSurveysKind,
  surveyPromptTemplate?: SurveyPromptTemplateTrackingInfo,
  defaultQuery?: boolean,
  recommendedActionId?: number,
) => {
  if (rank === 0) return;
  const searchScope: SurveyEventInfo['search_scope'] =
    filterInfo.searchScope === 'specified_files'
      ? {
          name: 'document_specify',
          document: {
            type: 'user_document' as const,
            id: filterInfo.specifiedFiles[0].id,
          },
          specified_method: method ?? null,
        }
      : {
          name: filterInfo.searchScope,
          ...(filterInfo.searchScope === 'group_marks'
            ? {
                group_id: filterInfo.groupId,
                group_tag_ids: filterInfo.groupTags,
              }
            : {}),
        };

  // シェアスレッドから関連質問の場合、シェアスレッドのリクエストIDが使用される。
  // 通常のスレッドの関連質問は直前のブロックのリクエストIDを使用する。
  let originalRequestId = null;
  if (fromRelatedQuestion) {
    originalRequestId = shareSurveyLastRequestId || previousRequestId || null;
  }

  const promptType: SurveyPromptType = {
    name: fromRelatedQuestion
      ? 'related_prompt'
      : defaultQuery
        ? 'template'
        : 'custom_input',
    original_request_id: originalRequestId,
  };

  await trackingSurveyEvent(SURVEY_FEATURE_NAME, {
    query: null,
    from: {
      type: from,
      share_surveys_kind: shareSurveyKind,
    },
    prompt_type: promptType,
    search_scope: searchScope,
    original_sample_session_id: sampleSessionId || null,
    rank: rank,
    parent_request_id: parentRequestId,
    data_sources: dataSources,
    data_sources_filter_used: filterInfo.docTypes.length > 0,
    deep_research_used: deepMode,
    session_id: sessionId,
    survey_type: surveyType,
    survey_prompt_template: surveyPromptTemplate,
    recommended_action:
      recommendedActionId !== undefined
        ? { id: recommendedActionId }
        : undefined,
  });
};

/**
 * Survey関連ページのページビュートラッキング
 * @params pageName ページ名
 * @params currentRoutePath 現在のルートのパス
 * @params surveySessionId スレッドのセッションID
 * @params shareSurveySessionId シェアスレッドのセッションID
 */
export const trackSurveyPageView = async (
  pageName: SurveyPageName,
  currentRoutePath: string,
  surveySessionId?: string,
  shareSurveySessionId?: string,
) => {
  await trackingSurveyEvent('page_view', {
    page: {
      name: pageName,
      url: currentRoutePath,
    },
    survey: {
      session_id: surveySessionId,
    },
    share_survey: {
      session_id: shareSurveySessionId,
    },
  });
};
